import React, { useEffect, useRef, useState } from 'react';
import { format } from 'date-fns';
import { jsPDF } from 'jspdf';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { addParticipantDocuments } from '../../../../store/documentService';
import { uuid } from '../../../../utils/stringUtils';

import pdf from '../../../../commonStyles/pdf.module.css';

const GenerateAppointmentPDF = ({
    children,
    onSavePDF,
    loggedInUserId,
    currentParticipantId,
    fileName,
    attendanceId,
    appointmentType,
    mandatoryAppointment,
    appointmentLocationId,
    exitFormTemplateId,
    doubtFormId,
    complianceActivityFormQuestionnaireId,
    isLoaded,
    currentAppointment
}) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const templateContainerRef = useRef(null);

    // LOCAL STATE
    const documentId =
        currentAppointment?.calendarAppointmentId?.slice(-9) || uuid().slice(-9) || '-';
    const [file, setFile] = useState({});

    // STORE STATE
    const documentTypes = useSelector(
        (state) => state.entities.directusService.documentTypeIdsAndNamesForContract
    );

    // USEEFFECTS
    useEffect(() => {
        if (!templateContainerRef?.current) return;
        isLoaded && generatePdf();
    }, [isLoaded]);

    useEffect(() => {
        if (file instanceof File && documentTypes?.length) uploadPdf();
    }, [file, documentTypes]);

    // HELPER FNS
    const createFooter = (doc) => {
        const pageCount = doc.internal.getNumberOfPages();
        const pageSize = doc.internal.pageSize;
        doc.setFontSize(8);
        Array.from({ length: pageCount }, (_, i) => i + 1).forEach((pageNumber) => {
            doc.setPage(pageNumber);
            const footerText = `    Page ${pageNumber} of ${pageCount} ${' '.repeat(110)}Printed on ${format(new Date(), 'EEEE, MMMM dd, yyyy HH:mm')} Document ID : ${documentId}`;
            doc.text(footerText, 1, pageSize.height - 3, { align: 'left' });
        });
    };

    const generatePdf = () => {
        const doc = new jsPDF();
        doc.setFontSize(12);
        doc.html(templateContainerRef.current, {
            async callback(doc) {
                createFooter(doc);
                doc.save(`${fileName}.pdf`);
                const pdfFile = new File([doc.output('blob')], `${fileName}.pdf`, {
                    type: 'application/pdf'
                });
                setFile(pdfFile);
            },
            autoPaging: 'text',
            margin: [10, 0, 10, 0],
            width: 210,
            windowWidth: 1050
        });
    };

    const uploadPdf = () => {
        const documentType = documentTypes.find(
            (el) => el.name.toLowerCase() === appointmentType.name.toLowerCase()
        );
        const payload = new FormData();
        payload.append('file', file);
        payload.append('userId', loggedInUserId);
        payload.append('typeId', documentType?.id);
        payload.append('participantId', currentParticipantId);
        payload.append('attendanceId', attendanceId);

        const msg = `Document: ${file.name} has been uploaded`;
        dispatch(addParticipantDocuments(currentParticipantId, payload, msg));
        onExit();
    };

    // EVENT HANDLERS
    const onExit = async () => {
        await onSavePDF();
        navigate('/edit_participant', {
            state: {
                attendanceStatusId: currentAppointment.attendanceStatusId,
                appointmentTypeName: appointmentType.name,
                mandatoryAppointment,
                appointmentLocationId,
                exitFormTemplateId,
                complianceActivityFormQuestionnaireId,
                doubtFormId
            }
        });
    };

    // RENDER
    return (
        <div className={pdf.previewPdfHiddenWrapper}>
            <div
                id="contentTemplateContainer"
                ref={templateContainerRef}
                data-testid="content_template_container">
                {children}
            </div>
        </div>
    );
};
GenerateAppointmentPDF.propTypes = {
    children: PropTypes.element,
    onSavePDF: PropTypes.func,
    loggedInUserId: PropTypes.string,
    currentParticipantId: PropTypes.string,
    fileName: PropTypes.string,
    attendanceId: PropTypes.string,
    appointmentType: PropTypes.object,
    mandatoryAppointment: PropTypes.bool,
    appointmentLocationId: PropTypes.string,
    exitFormTemplateId: PropTypes.string,
    doubtFormId: PropTypes.string,
    complianceActivityFormQuestionnaireId: PropTypes.string,
    isLoaded: PropTypes.bool,
    currentAppointment: PropTypes.object
};

export default GenerateAppointmentPDF;
